import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';

export interface RecalculationProgress {
    jobId: string;
    totalItems: number;
    completed: number;
    failed: number;
    remaining: number;
    componentIndividualId: number;
    flowExecutionId: number;
    isSuccess: boolean;
}

export interface RecalculationCompleted {
    jobId: string;
    status: number;
    message: string;
    totalItems: number;
    completed: number;
    failed: number;
}

export type RecalculationEvent =
    | { type: 'started'; totalItems: number }
    | { type: 'progress'; data: RecalculationProgress }
    | { type: 'completed'; data: RecalculationCompleted };

@Injectable({ providedIn: 'root' })
export class RecalculationHubService {
    private hubConnection: signalR.HubConnection | null = null;

    private readonly _progress$ = new Subject<RecalculationProgress>();
    private readonly _completed$ = new Subject<RecalculationCompleted>();

    readonly progress$ = this._progress$.asObservable();
    readonly completed$ = this._completed$.asObservable();

    get connectionId(): string | null {
        return this.hubConnection?.connectionId ?? null;
    }

    constructor(private msalService: MsalService, private ngZone: NgZone) { }

    async startConnection(): Promise<void> {
        if (this.hubConnection) {
            console.log('[RecalculationHub] Connection already established. connectionId:', this.hubConnection.connectionId);
            return;
        }

        await this.msalService.instance.initialize();

        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        const claims = account?.idTokenClaims as Record<string, any>;
        const userId = claims?.['userid'] ?? account?.username;

        const hubUrl = `${environment.protectedResources.api.endpoint}/hubs/recalculation`;
        console.log('[RecalculationHub] Connecting to:', hubUrl, 'UserId:', userId);

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => this.getAccessToken(),
            })
            .withAutomaticReconnect()
            .build();

        this.hubConnection.onreconnecting((error) =>
            console.warn('[RecalculationHub] Reconnecting...', error)
        );

        this.hubConnection.onreconnected(async (connectionId) => {
            console.log('[RecalculationHub] Reconnected. New connectionId:', connectionId);
            if (userId) {
                await this.hubConnection!.invoke("JoinUserGroup", userId);
            }
        });

        this.hubConnection.onclose((error) =>
            console.error('[RecalculationHub] Connection closed.', error)
        );

        this.hubConnection.on('RecalculationProgress', (data: RecalculationProgress) => {
            console.log(`[RecalculationHub] Progress (${data.completed}/${data.totalItems}) jobId=${data.jobId}`, data);
            this.ngZone.run(() => this._progress$.next(data));
        });

        this.hubConnection.on('RecalculationCompleted', (data: RecalculationCompleted) => {
            console.log(`[RecalculationHub] Completed jobId=${data.jobId}`, data);
            this.ngZone.run(() => this._completed$.next(data));
        });

        await this.hubConnection.start();
        console.log('[RecalculationHub] Connected successfully. connectionId:', this.hubConnection.connectionId);

        if (userId) {
            await this.hubConnection.invoke("JoinUserGroup", userId);
            console.log('[RecalculationHub] Joined group:', userId);
        }
    }

    async stopConnection(): Promise<void> {
        if (this.hubConnection) {
            console.log('[RecalculationHub] Stopping connection. connectionId:', this.hubConnection.connectionId);
            this.hubConnection.off('RecalculationProgress');
            this.hubConnection.off('RecalculationCompleted');
            await this.hubConnection.stop();
            this.hubConnection = null;
            console.log('[RecalculationHub] Connection stopped.');
        }
    }

    private getAccessToken(): Promise<string> {
        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        return this.msalService.instance
            .acquireTokenSilent({
                scopes: environment.protectedResources.api.scopes,
                account,
            })
            .then((result) => {
                console.log('[RecalculationHub] Access token acquired.');
                return result.accessToken;
            });
    }
}
